// Saved reply templates — Phase 6e + Polish #15.
//
// Two shapes live in this file now:
//
//   kind: 'faq'     — Long-form FAQ answers Kimi uses verbatim when a
//                     customer asks a policy-ish question (turnaround,
//                     refunds, proofs, etc.). Content was lifted from her
//                     approved FAQ copy (refund/turnaround/customization
//                     memories from 2026-04-16). If she updates the FAQ,
//                     update these too.
//
//   kind: 'action'  — Polish #15. Short, task-shaped replies for the
//                     common outbound actions: send tracking, request
//                     proof approval, confirm a reship, confirm an
//                     address change, post a status update. These
//                     carry `{name}`, `{order}`, and `{tracking}`
//                     placeholders that the composer fills at pick
//                     time using order context + customer display name.
//                     If a placeholder is missing (no tracking# on
//                     file, for example) the literal `{tracking}` is
//                     left in the body so Kimi sees immediately where
//                     she still needs to type something.
//
// MVP scope: no DB. The templates live in code. If Kimi starts editing
// them regularly, we'll move them to a saved_replies table with RLS
// scoped to her user id.
//
// Rules the copy follows:
//   - No em dashes (Kimi's hard rule — tells AI apart from human).
//   - No pricing figures (her "no pricing in content" rule).
//   - Signature auto-appended server-side, so templates end mid-thought
//     without "— Kimi, Market Dwellings".
//   - Tone: warm, direct, no parroting. Matches her draft voice memory.

/** Which group the template belongs to in the picker. */
export type SavedReplyKind = 'action' | 'faq';

export type SavedReply = {
  /** Stable id — used as React key, also handy if we ever persist. */
  id: string;
  /** Which grouping this template falls under in the picker. */
  kind: SavedReplyKind;
  /** Short label shown in the menu. */
  label: string;
  /** One-line hint shown beneath the label. */
  description: string;
  /**
   * The body text inserted into the composer. May contain `{name}`,
   * `{order}`, and `{tracking}` placeholders for action templates;
   * FAQ templates don't use placeholders. Unfilled placeholders are
   * left literal so Kimi can see what she still needs to type.
   */
  body: string;
  /**
   * Autofill placeholders the action template expects. Used to drive
   * the "needs {order}" warning chip in the menu when context is
   * missing. FAQ templates use an empty array.
   */
  placeholders: Array<'name' | 'order' | 'tracking'>;
  /**
   * Slash-command keyword for Polish #19. Typing `/{slash}` in the
   * composer pulls this template up in the inline popover. No leading
   * slash here — just the bare keyword (e.g. `proof`, `tracking`).
   * Omit on templates that shouldn't be reachable via slash.
   */
  slash?: string;
};

export const SAVED_REPLIES: SavedReply[] = [
  // ── Quick action templates (Polish #15) ──────────────────────────────
  // Short, task-shaped, autofilled. Ordered by how often Kimi
  // reaches for them: tracking asks are her #1 inbound.
  {
    id: 'send-tracking',
    kind: 'action',
    label: 'Send tracking link',
    description: 'Drops in the latest tracking for the order',
    placeholders: ['order', 'tracking'],
    slash: 'tracking',
    body:
`Here's the latest on {order}: {tracking}

If that link isn't pulling anything up yet, carrier scans can take a day or two to catch up once a label prints. Let me know if you still don't see movement after that.`,
  },
  {
    id: 'request-proof-approval',
    kind: 'action',
    label: 'Request proof approval',
    description: 'Sends the proof and asks for the green light',
    placeholders: ['name', 'order'],
    slash: 'proof',
    body:
`Here's the proof for {order}, {name}. Have a look and let me know if anything needs tweaking, or reply with the go-ahead and I'll get production started.

Once you approve, we'll move to print the same business day.`,
  },
  {
    id: 'apologize-reship',
    kind: 'action',
    label: 'Apologize + reship',
    description: 'Owns the issue and queues a replacement',
    placeholders: ['name', 'order'],
    slash: 'reship',
    body:
`So sorry about the trouble with {order}, {name}. I'm queuing a reshipment now at no charge, and I'll send the new tracking number the moment the replacement label prints.

You can keep or toss whatever showed up damaged, whichever is easier on your end.`,
  },
  {
    id: 'address-change-confirmed',
    kind: 'action',
    label: 'Address change confirmed',
    description: 'Confirms a shipping address update pre-production',
    placeholders: ['name', 'order'],
    slash: 'address',
    body:
`Got it, {name}. I've updated the shipping address on {order} before production started, so you're all set. The new address will be on the label when we ship.`,
  },
  {
    id: 'status-update',
    kind: 'action',
    label: 'Order status update',
    description: 'Wraps a "where are we?" reply when things are still on track',
    placeholders: ['order'],
    slash: 'status',
    body:
`Thanks for checking in on {order}. We're wrapping production this week and it should ship in the next day or two. I'll send a tracking number the moment the label prints, so you won't have to guess.`,
  },

  // ── FAQ answers (Phase 6e) ──────────────────────────────────────────
  // Long-form. Used when the customer asks a policy question more than
  // a transactional one. No autofill.
  {
    id: 'refund-cancel',
    kind: 'faq',
    label: 'Refund or cancellation',
    description: 'Made-to-order, no returns / exchanges',
    placeholders: [],
    slash: 'refund',
    body:
`Thanks for reaching out. Because every Market Dwellings product is custom-printed to order with your personalized information, we aren't able to accept returns or exchanges once an order is placed.

If something arrived damaged or there's a production issue on our end, send us a photo within 14 days of delivery and we'll make it right.

Let me know if you'd like me to take a closer look at your order.`,
  },
  {
    id: 'turnaround',
    kind: 'faq',
    label: 'Turnaround time',
    description: 'Standard production + transit windows',
    placeholders: [],
    slash: 'turnaround',
    body:
`Happy to help with timing. Standard production runs 1 to 3 business days from the time your proof is approved (or from the time you place a non-proof order). After that, USPS or UPS transit is typically 2 to 5 business days depending on your zip code.

If you have a specific deadline, let me know and I'll double-check what's realistic.`,
  },
  {
    id: 'expedited',
    kind: 'faq',
    label: 'Expedited / rush',
    description: 'Expedited speeds shipping, not production',
    placeholders: [],
    slash: 'rush',
    body:
`Great question on rush options. Our expedited shipping option speeds up the carrier transit once your order leaves our shop, but it doesn't speed up production itself. Production still runs 1 to 3 business days.

If you need the fastest possible turnaround, expedited shipping plus a quick proof approval is the combination that gets there soonest.`,
  },
  {
    id: 'brokerage-pickup',
    kind: 'faq',
    label: 'Brokerage pickup',
    description: 'Local pickup for brokerage partners',
    placeholders: [],
    slash: 'pickup',
    body:
`Yes, local pickup is available for our brokerage partners. Once your order finishes production I'll send you a note that it's ready, and you can swing by to grab it at your convenience.

Want me to flag this order as pickup instead of shipping?`,
  },
  {
    id: 'customization-how',
    kind: 'faq',
    label: 'Customization how-to',
    description: 'Editor basics + what can / cannot change',
    placeholders: [],
    slash: 'editor',
    body:
`Most of our products are fully customizable through our online design editor. You can change colors, swap fonts, and edit the text right on the product page before you check out.

A few things to know: hand-lettered pieces can't be retraced, and business card slots are a fixed 3.5 by 2 inch size. Everything else is fair game.

If you run into any trouble in the editor, screenshot what you're seeing and I'll walk you through it.`,
  },
  {
    id: 'logo-headshot',
    kind: 'faq',
    label: 'Logo / headshot upload',
    description: 'SVG preferred, upload tips',
    placeholders: [],
    slash: 'logo',
    body:
`For logos and headshots, the editor lets you upload your own image right from the product page. For logos, an SVG file gives you the sharpest result at any size. If you only have a PNG or JPG, aim for the largest, highest-quality version you have on hand.

Headshots work best as a square crop with your face centered. If the upload tool is giving you trouble, send the file over and I can prep it for you.`,
  },
  {
    id: 'proof-request',
    kind: 'faq',
    label: 'Proof request',
    description: 'How proofs work + approval step',
    placeholders: [],
    slash: 'proofinfo',
    body:
`Sure, I can get a proof started for you. Once you place the order with the note that you'd like a proof, we'll send over a digital version for you to review before anything goes to print.

You can request small tweaks at that stage. Production doesn't start until you give the green light, so there's no rush on our side.`,
  },
  {
    id: 'order-note',
    kind: 'faq',
    label: 'Order-note instruction',
    description: 'Critical: how to leave a special request',
    placeholders: [],
    slash: 'note',
    body:
`Got it. The easiest way to make sure a special request reaches our production team is to leave it as a note in your cart before you check out. There's a "Notes" or "Special instructions" field right on the cart page.

Anything you type there gets attached directly to your order, so it lands in front of us the moment we start on it. Let me know if you'd like me to add a note to an order that's already placed.`,
  },
];

/**
 * Context values the composer knows about the current thread. Passed into
 * `fillSavedReply()` to resolve `{name}`, `{order}`, and `{tracking}`
 * placeholders.
 */
export interface SavedReplyContext {
  /** Best-available customer display name (first+last > displayName > email local). */
  name: string | null;
  /** Order number hint — either from intent classifier or the primary order. */
  order: string | null;
  /** Tracking number OR tracking URL string — whichever we have. */
  tracking: string | null;
}

/**
 * Substitute placeholder tokens in a template body. If a value is missing
 * we leave the literal `{key}` in place so Kimi can see exactly where she
 * still needs to type something rather than silently producing "Here's the
 * latest on : ." Same pattern as TeamPingPanel's fillTemplate.
 */
export function fillSavedReply(reply: SavedReply, ctx: SavedReplyContext): string {
  return reply.body
    .replaceAll('{name}', ctx.name ?? '{name}')
    .replaceAll('{order}', ctx.order ?? '{order}')
    .replaceAll('{tracking}', ctx.tracking ?? '{tracking}');
}

/**
 * Which placeholders for this template are NOT going to be filled with the
 * current context. Used by the menu to render a small "needs order #" chip
 * next to the template so Kimi knows before she picks it.
 */
export function missingPlaceholders(
  reply: SavedReply,
  ctx: SavedReplyContext,
): Array<'name' | 'order' | 'tracking'> {
  return reply.placeholders.filter((p) => {
    if (p === 'name') return !ctx.name;
    if (p === 'order') return !ctx.order;
    if (p === 'tracking') return !ctx.tracking;
    return false;
  });
}

// ── Slash commands (Polish #19) ──────────────────────────────────────────

/**
 * A detected in-progress slash token inside a textarea.
 *
 *   start   — index of the leading `/` in the textarea value
 *   end     — cursor position (exclusive end of the token)
 *   query   — the characters typed after `/`, lowercased. Empty string
 *             when the user just typed `/` with nothing after it yet.
 *
 * Coordinates are character offsets into the raw textarea value, so the
 * caller can splice them out and reinsert the resolved template body.
 */
export interface SlashToken {
  start: number;
  end: number;
  query: string;
}

/**
 * Scan a textarea value and cursor position for an in-progress slash
 * command. Returns null when the cursor isn't inside one. A slash is
 * considered "in progress" when:
 *
 *   - There's a `/` at the start of the current word
 *   - The `/` is either at the start of the text or preceded by whitespace
 *     (so `user/foo` doesn't false-trigger inside URLs or emails)
 *   - Everything after the `/` up to the cursor is letters or a hyphen
 *     (so `/proof then a space` closes the token and the popover dismisses)
 *
 * This mirrors how Slack + Linear detect slash commands — the popover
 * opens the moment you type `/`, filters as you keep typing, and closes
 * the moment you type anything that couldn't be a command keyword.
 */
export function findActiveSlash(value: string, cursor: number): SlashToken | null {
  if (cursor < 1) return null;
  // Walk back from the cursor to find the start of the current token.
  let i = cursor;
  while (i > 0) {
    const ch = value.charAt(i - 1);
    if (ch === '/') {
      // Must be at start-of-text or preceded by whitespace.
      const prev = i - 2 >= 0 ? value.charAt(i - 2) : '';
      if (prev === '' || /\s/.test(prev)) {
        const query = value.slice(i, cursor).toLowerCase();
        return { start: i - 1, end: cursor, query };
      }
      return null;
    }
    // Only a–z / A–Z / hyphen are valid query chars. Anything else ends
    // the candidate (including spaces, punctuation, digits — Kimi's slash
    // keywords are all alpha).
    if (!/[a-zA-Z-]/.test(ch)) return null;
    i--;
  }
  return null;
}

/**
 * Filter SAVED_REPLIES entries whose slash keyword starts with `query`.
 * Case-insensitive. Returns up to `limit` matches in the order they
 * appear in SAVED_REPLIES (action templates first, FAQ second — same
 * priority as the Templates menu).
 */
export function matchSlashReplies(
  query: string,
  limit = 6,
): SavedReply[] {
  const q = query.toLowerCase();
  const out: SavedReply[] = [];
  for (const r of SAVED_REPLIES) {
    if (!r.slash) continue;
    if (r.slash.startsWith(q)) {
      out.push(r);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/**
 * Splice a resolved template body into a textarea value, replacing the
 * slash token. Returns the new value plus the cursor position where the
 * caret should end up (right after the inserted body).
 */
export function applySlashReply(
  value: string,
  token: SlashToken,
  resolvedBody: string,
): { value: string; cursor: number } {
  const before = value.slice(0, token.start);
  const after = value.slice(token.end);
  const next = before + resolvedBody + after;
  return { value: next, cursor: (before + resolvedBody).length };
}
